import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import type { AxiosResponse } from 'axios';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import type ApiReservation from 'src/core/models/api/ApiReservation';
import type { ApiReservationId } from 'src/core/models/api/ApiReservation';
import { isApiValidationErrorResponse } from 'src/core/models/api/ApiValidationErrorResponse';
import type ApiErrorResponse from 'src/core/models/api/responses/ApiErrorResponse';
import type ApiResourceResponse from 'src/core/models/api/responses/ApiResourceResponse';
import type ApiReservationsResponse from 'src/core/models/api/responses/ApiReservationsResponse';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import type ReservationsScreenType from 'src/domain/models/ReservationsScreenType';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const noValidationErrors: Record<string, string[]> = {};

const initialState = {
	upcomingReservations: null as ApiReservationsResponse | null,
	historicalReservations: null as ApiReservationsResponse | null,
	isLoadingReservations: false,
	hasReservationsError: false,
	isAddReservationManuallyDialogVisible: false,
	reservation: null as ApiReservation | null,
	isLoadingReservation: false,
	hasReservationError: false,
	validationErrors: noValidationErrors,
	replyMessage: '',
	isReplyingToReservation: false,
	isReplyDialogVisible: false,
	isReplySentDialogVisible: false,
	isBarcodeDialogVisible: false,
	isAddingReservation: false,
	addReservationErrorMessage: null as string | null,
};

export const useReservationsStore = create(
	combine({ ...initialState }, (set, get) => {
		const getApiReservations = async ({
			screenType,
			onSuccess,
			onError,
			onFinally,
		}: {
			screenType: ReservationsScreenType;
			onSuccess?: (response: AxiosResponse<ApiReservationsResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			const isPastOrder = screenType.isPastOrder;

			const loadedList = isPastOrder ? get().historicalReservations : get().upcomingReservations;
			if (loadedList === null) {
				set({ isLoadingReservations: true });
			}
			set({ hasReservationsError: false });

			const request = isPastOrder
				? ThermenRepository.getHistoricalReservations()
				: ThermenRepository.getUpcomingReservations();

			await request
				.then(async (response) => {
					set(isPastOrder ? { historicalReservations: response.data } : { upcomingReservations: response.data });
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ hasReservationsError: (isPastOrder ? get().historicalReservations : get().upcomingReservations) === null });
					reportToCallerOrCrashlytics({ message: 'Fetching the reservations failed.', error, onError });
				})
				.finally(() => {
					set({ isLoadingReservations: false });
					onFinally?.();
				});
		};

		const getApiReservation = async ({
			reservationId,
			onSuccess,
			onError,
			onFinally,
		}: {
			reservationId: ApiReservationId;
			onSuccess?: (response: AxiosResponse<ApiResourceResponse<ApiReservation>>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			if (get().reservation?.id !== reservationId) {
				set({ reservation: null, isLoadingReservation: true });
			}
			set({ hasReservationError: false });

			await ThermenRepository.findReservation<ApiReservation>(reservationId)
				.then(async (response) => {
					set({ reservation: response.data.data });
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ hasReservationError: get().reservation === null });
					reportToCallerOrCrashlytics({ message: 'Fetching the reservation failed.', error, onError });
				})
				.finally(() => {
					set({ isLoadingReservation: false });
					onFinally?.();
				});
		};

		const postApiReservationReply = async ({
			reservationId,
			replyMessage,
			onSuccess,
			onError,
			onFinally,
		}: {
			reservationId: ApiReservationId;
			replyMessage: string;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ validationErrors: {}, isReplyingToReservation: true });

			await ThermenRepository.replyToReservation(reservationId, replyMessage)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => {
					if (isAxiosError(error) && error.response && isApiValidationErrorResponse(error.response.data)) {
						set({ validationErrors: error.response.data.errors });
					}
					reportToCallerOrCrashlytics({ message: 'Replying to a reservation failed.', error, onError });
				})
				.finally(() => {
					set({ isReplyingToReservation: false });
					onFinally?.();
				});
		};

		const postApiAddReservationManually = async ({
			workspaceId,
			reservationNumber,
			onSuccess,
			onError,
			onFinally,
		}: {
			workspaceId: string;
			reservationNumber: string;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isAddingReservation: true, addReservationErrorMessage: null });

			await ThermenRepository.addReservationManually(workspaceId, reservationNumber)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => {
					let message = Translations.somethingWentWrongTitle;

					if (isAxiosError<ApiErrorResponse>(error)) {
						const errorResponse = error.response;
						const responseMessage = errorResponse?.data?.message;

						if (errorResponse?.status === Constants.httpStatusUnprocessableEntity && responseMessage) {
							message = responseMessage;
						}
					}

					set({ addReservationErrorMessage: message });
					reportToCallerOrCrashlytics({ message: 'Adding a reservation manually failed.', error, onError });
				})
				.finally(() => {
					set({ isAddingReservation: false });
					onFinally?.();
				});
		};

		const clearValidationError = (field: string) => {
			const validationErrors = { ...get().validationErrors };
			delete validationErrors[field];
			set({ validationErrors });
		};

		const setAddReservationErrorMessage = (message: string | null) => set({ addReservationErrorMessage: message });

		const setAddReservationManuallyDialogVisible = (isAddReservationManuallyDialogVisible: boolean) =>
			set({ isAddReservationManuallyDialogVisible });

		const setReplyMessage = (replyMessage: string) => set({ replyMessage });

		const openReplyDialog = () => set({ validationErrors: {}, isReplyDialogVisible: true });

		const closeReplyDialog = () => set({ isReplyDialogVisible: false });

		const setReplySentDialogVisible = (isReplySentDialogVisible: boolean) => set({ isReplySentDialogVisible });

		const openBarcodeDialog = () => set({ isBarcodeDialogVisible: true });

		const closeBarcodeDialog = () => set({ isBarcodeDialogVisible: false });

		const reset = () => set({ ...initialState });

		return {
			getApiReservations,
			getApiReservation,
			postApiReservationReply,
			postApiAddReservationManually,
			clearValidationError,
			setAddReservationErrorMessage,
			setAddReservationManuallyDialogVisible,
			setReplyMessage,
			openReplyDialog,
			closeReplyDialog,
			setReplySentDialogVisible,
			openBarcodeDialog,
			closeBarcodeDialog,
			reset,
		};
	}),
);
